import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  PAYROLL_RUN_STATUS_MAP,
  tafqit,
  type Branding,
  type CompanyProfile,
  type PayrollRunStatus,
  type TenantSettings,
} from '@goyone/shared';
import { api } from '../../lib/api';
import { closePrintView } from '../../lib/printclose';
import { money, num } from '../../lib/format';
import { Logo } from '../../components/Logo';
import { PaperBackground, paperPageProps, useDocumentPaper } from '../../components/DocumentPaper';
import '../../styles/print.css';

/**
 * قسيمة عمولات الشهر — مجمَّعةً لكلّ المندوبين.
 *
 * قسيمةُ الفرد تُعطى له، وهذه تُحفظ في ملفّ الشهر: من أين جاء المال،
 * وكم خرج منه حافزًا، ولمن. فمن يراجع الشهر بعد سنةٍ يجد ورقةً واحدة
 * تجيبه، لا اثنتي عشرة قسيمةً يجمعها بنفسه.
 *
 * وأرقامُها من الكشف المجمَّد لا من حسابٍ جديد: تعديلُ نسبةٍ اليومَ لا
 * يغيّر شهرًا صُرف.
 */

interface Profile {
  tenant: { name: string; currency: string };
  branding: Branding;
  company: CompanyProfile;
  settings: TenantSettings;
}

interface Line {
  id: string;
  employeeName: string;
  employeeCode: string | null;
  jobTitle: string | null;
  basisAmount: number;
  target: number | null;
  rate: number;
  eligible: number;
  commission: number;
  documentCount: number;
  achievement: number | null;
  blockedReason: string | null;
}

interface RunData {
  id: string;
  periodLabel: string;
  status: PayrollRunStatus;
  basis: 'COLLECTED' | 'INVOICED';
  basisLabel: string;
  targetPolicyLabel: string;
  defaultRate: number;
  total: number;
  repCount: number;
  notes: string | null;
  approvedAt: string | null;
  totals: {
    invoiced: number;
    invoiceCount: number;
    collected: number;
    paymentCount: number;
  };
  lines: Line[];
}

export function PrintCommissionRun() {
  const { id = '' } = useParams<{ id: string }>();

  const run = useQuery({
    queryKey: ['commissions', 'run', id],
    queryFn: () => api<RunData>(`/commissions/runs/${id}`),
  });
  const profile = useQuery({
    queryKey: ['settings'],
    queryFn: () => api<Profile>('/settings'),
  });
  /* ورقةُ الشركة (ليتر هيد) إن رُفعت: تحلّ محلّ ترويسة النظام وتذييله. */
  const { paper, ready: paperReady } = useDocumentPaper('letterhead');

  const d = run.data;
  const p = profile.data;

  if (run.isError) {
    return <div className="print-root">تعذّر تحميل الكشف.</div>;
  }
  if (!d || !p || !paperReady) {
    return <div className="print-root">جارٍ التحضير…</div>;
  }

  const { tenant, branding, company, settings } = p;
  const currency = settings.currencyLabel || 'ج.م';
  const collected = d.basis === 'COLLECTED';
  const t = d.totals;
  const collectPct =
    t.invoiced > 0 ? Math.round((t.collected / t.invoiced) * 100) : 0;
  const costPct =
    t.collected > 0 ? Math.round((d.total / t.collected) * 100) : 0;
  const earners = d.lines.filter((l) => l.commission > 0);

  const summary: Array<[string, string]> = [
    ['إجمالي المبيعات (المفوتر)', money(t.invoiced, currency)],
    ['إجمالي المحصَّل', `${money(t.collected, currency)} — ${collectPct}٪ من المفوتر`],
    ['أساس احتساب العمولة', d.basisLabel],
    ['سياسة الهدف', d.targetPolicyLabel],
    ['عدد المندوبين في الكشف', `${num(d.repCount)}`],
    ['المستحقّون فعلًا', `${num(earners.length)}`],
  ];

  return (
    <div
      className="print-root"
      style={{
        ['--doc-primary' as string]: branding.primary,
        ['--doc-ink' as string]: branding.ink,
      }}
    >
      <div className="print-toolbar no-print">
        <button className="btn btn-primary" onClick={() => window.print()}>
          طباعة أو حفظ PDF
        </button>
        <button className="btn" onClick={closePrintView}>
          إغلاق
        </button>
        <span className="small mute">
          اختر «حفظ كـ PDF» في نافذة الطباعة للحصول على ملف.
        </span>
      </div>

      <article {...paperPageProps(paper, 'print-page')}>
        <PaperBackground paper={paper} />
        <header className="doc-header">
          <div className="doc-brand">
            {branding.logoUrl ? (
              <img src={branding.logoUrl} alt="" className="doc-logo" />
            ) : (
              <Logo size={52} />
            )}
            <div>
              <div className="doc-company">{company.legalName || tenant.name}</div>
              {branding.documentHeader && (
                <div className="doc-tagline">{branding.documentHeader}</div>
              )}
              <div className="doc-company-meta">
                {company.address && <div>{company.address}</div>}
                <div className="doc-inline">
                  {company.phone && <span className="num">{company.phone}</span>}
                  {company.email && <span className="num">{company.email}</span>}
                </div>
              </div>
            </div>
          </div>

          <div className="doc-title-block">
            <div className="doc-kind">قسيمة عمولات الشهر</div>
            <div className="doc-number">{d.periodLabel}</div>
          </div>
        </header>

        <section className="doc-parties">
          <div className="doc-party">
            <div className="doc-label">الكشف</div>
            <div className="doc-party-name">عمولات {d.periodLabel}</div>
            <div>
              الحالة: {PAYROLL_RUN_STATUS_MAP[d.status].label}
            </div>
          </div>

          <div className="doc-dates">
            <div className="doc-date-row">
              <span className="doc-label">إجمالي العمولات</span>
              <span className="num">{money(d.total, currency)}</span>
            </div>
            <div className="doc-date-row">
              <span className="doc-label">كلفتها من المحصَّل</span>
              <span className="num">{costPct}٪</span>
            </div>
          </div>
        </section>

        {/* أوّلًا: من أين جاء المال — الرقمُ الذي تُقتطع منه العمولة. */}
        <h3 style={{ marginTop: 4, marginBottom: 8 }}>حركة الشهر</h3>
        <table className="doc-table">
          <thead>
            <tr>
              <th>البيان</th>
              <th style={{ width: 220 }}>القيمة</th>
            </tr>
          </thead>
          <tbody>
            {summary.map(([label, value]) => (
              <tr key={label}>
                <td>{label}</td>
                <td className="num end">{value}</td>
              </tr>
            ))}
            <tr>
              <td className="strong">إجمالي العمولات المستحقّة</td>
              <td className="num end strong">{money(d.total, currency)}</td>
            </tr>
          </tbody>
        </table>

        <h3 style={{ marginTop: 20, marginBottom: 8 }}>المستحقّون</h3>
        <table className="doc-table">
          <thead>
            <tr>
              <th>المندوب</th>
              <th style={{ width: 90 }}>الكود</th>
              <th style={{ width: 130 }}>
                {collected ? 'المحصَّل' : 'المفوتر'}
              </th>
              <th style={{ width: 70 }}>المستندات</th>
              <th style={{ width: 120 }}>الخاضع</th>
              <th style={{ width: 60 }}>النسبة</th>
              <th style={{ width: 120 }}>العمولة</th>
            </tr>
          </thead>
          <tbody>
            {d.lines.length === 0 && (
              <tr>
                <td colSpan={7} className="center">
                  لا مندوبين في هذا الكشف.
                </td>
              </tr>
            )}
            {d.lines.map((l) => (
              <tr key={l.id}>
                <td>
                  {l.employeeName}
                  {l.blockedReason && (
                    <div className="small" style={{ color: '#8a5a00' }}>
                      {l.blockedReason}
                    </div>
                  )}
                </td>
                <td className="num center">{l.employeeCode ?? '—'}</td>
                <td className="num end">{money(l.basisAmount, currency)}</td>
                <td className="num center">{num(l.documentCount)}</td>
                <td className="num end">{money(l.eligible, currency)}</td>
                <td className="num center">{num(l.rate)}٪</td>
                <td className="num end">{money(l.commission, currency)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={6} className="strong">
                الإجمالي
              </td>
              <td className="num end strong">{money(d.total, currency)}</td>
            </tr>
          </tfoot>
        </table>

        <div className="doc-words" style={{ marginTop: 16 }}>
          <div className="doc-label">إجمالي العمولات بالحروف</div>
          <div className="doc-words-text">
            {d.total > 0
              ? tafqit(d.total, tenant.currency)
              : 'لا عمولات مستحقّة عن هذا الشهر.'}
          </div>
        </div>

        {d.notes && (
          <p className="small" style={{ marginTop: 12 }}>
            ملاحظات: {d.notes}
          </p>
        )}

        <p className="small" style={{ marginTop: 16, color: '#666' }}>
          قواعد الاحتساب مجمَّدة في هذا الكشف لحظة إنشائه — تعديل النسب أو
          الأهداف بعد ذلك لا يغيّر أرقامه. ولكلّ مندوبٍ قسيمةٌ مفصَّلة تُبيّن
          المستندات التي جاءت منها عمولته.
        </p>
      </article>
    </div>
  );
}
